import React, { useState } from 'react';
import { Globe, ArrowRight, RefreshCw, CheckCircle, AlertCircle, Edit3, Settings } from 'lucide-react';
import { AIProviderModal } from './AIProviderModal';
import { AIProvider, AutoModeProgress } from '../types';

interface AutoModeFormProps {
  onGenerate: (data: {
    websiteUrl: string;
    crawlMode: 'quick' | 'indepth';
    sitemapUrl?: string;
    provider: AIProvider;
    model: string;
    apiKey: string;
  }) => void;
  loading: boolean;
  progress?: AutoModeProgress;
  generatedContent?: string;
  onContentEdit: (content: string) => void;
  onFinalizeContent: () => void;
}

export function AutoModeForm({
  onGenerate,
  loading,
  progress,
  generatedContent,
  onContentEdit,
  onFinalizeContent
}: AutoModeFormProps) {
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [crawlMode, setCrawlMode] = useState<'quick' | 'indepth'>('quick');
  const [sitemapUrl, setSitemapUrl] = useState('');
  const [selectedProvider, setSelectedProvider] = useState<AIProvider>('openai');
  const [selectedModel, setSelectedModel] = useState('');
  const [apiKeys, setApiKeys] = useState<Record<AIProvider, string>>({
    openai: '',
    gemini: '',
    openrouter: ''
  });
  const [validationStatus, setValidationStatus] = useState<Record<AIProvider, 'idle' | 'validating' | 'valid' | 'invalid'>>({
    openai: 'idle',
    gemini: 'idle',
    openrouter: 'idle'
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingContent, setEditingContent] = useState(false);
  const [editedContent, setEditedContent] = useState('');

  const handleApiKeyChange = (provider: AIProvider, apiKey: string) => {
    setApiKeys(prev => ({ ...prev, [provider]: apiKey }));
    // Reset validation status when key changes
    setValidationStatus(prev => ({ ...prev, [provider]: 'idle' }));
  };

  const handleValidateKey = async (provider: AIProvider, apiKey: string): Promise<boolean> => {
    setValidationStatus(prev => ({ ...prev, [provider]: 'validating' }));
    
    try {
      // TODO: Implement actual API key validation
      // For now, simulate validation
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Basic validation - check if key format looks correct
      let isValid = false;
      switch (provider) {
        case 'openai':
          isValid = apiKey.startsWith('sk-') && apiKey.length > 20;
          break;
        case 'gemini':
          isValid = apiKey.startsWith('AIza') && apiKey.length > 20;
          break;
        case 'openrouter':
          isValid = apiKey.startsWith('sk-or-') && apiKey.length > 20;
          break;
      }
      
      setValidationStatus(prev => ({ ...prev, [provider]: isValid ? 'valid' : 'invalid' }));
      return isValid;
    } catch (error) {
      setValidationStatus(prev => ({ ...prev, [provider]: 'invalid' }));
      return false;
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!websiteUrl.trim()) {
      alert('Please enter a website URL');
      return;
    }
    
    if (validationStatus[selectedProvider] !== 'valid') {
      alert('Please validate your API key before proceeding');
      return;
    }

    if (!selectedModel) {
      alert('Please select a model before proceeding');
      return;
    }
    
    onGenerate({
      websiteUrl: websiteUrl.trim(),
      crawlMode,
      sitemapUrl: crawlMode === 'indepth' ? sitemapUrl.trim() : undefined,
      provider: selectedProvider,
      model: selectedModel,
      apiKey: apiKeys[selectedProvider]
    });
  };

  const handleEditContent = () => {
    setEditedContent(generatedContent || '');
    setEditingContent(true);
  };

  const handleSaveEdit = () => {
    onContentEdit(editedContent);
    setEditingContent(false);
  };

  const handleCancelEdit = () => {
    setEditingContent(false);
    setEditedContent('');
  };

  const isFormValid = websiteUrl.trim() && validationStatus[selectedProvider] === 'valid' && selectedModel;

  return (
    <div className="space-y-8">
      {/* Website URL Input */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Website URL</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Globe className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="url"
              placeholder="https://example.com"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              disabled={loading}
              className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
              required
            />
          </div>
          {/* Crawl Mode Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Analysis Mode
            </label>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <input
                  type="radio"
                  id="quick-mode"
                  name="crawlMode"
                  value="quick"
                  checked={crawlMode === 'quick'}
                  onChange={(e) => setCrawlMode(e.target.value as 'quick' | 'indepth')}
                  className="mt-1 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300"
                  disabled={loading}
                />
                <div className="flex-1">
                  <label htmlFor="quick-mode" className="text-sm font-medium text-gray-900 cursor-pointer">
                    Quick Mode (Recommended)
                  </label>
                  <p className="text-xs text-gray-500">
                    Analyzes navigation and footer links for key pages (5-10 pages)
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <input
                  type="radio"
                  id="indepth-mode"
                  name="crawlMode"
                  value="indepth"
                  checked={crawlMode === 'indepth'}
                  onChange={(e) => setCrawlMode(e.target.value as 'quick' | 'indepth')}
                  className="mt-1 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300"
                  disabled={loading}
                />
                <div className="flex-1">
                  <label htmlFor="indepth-mode" className="text-sm font-medium text-gray-900 cursor-pointer">
                    In-Depth Mode
                  </label>
                  <p className="text-xs text-gray-500">
                    Uses sitemap.xml to analyze comprehensive site structure (up to 20 pages)
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Sitemap URL for In-Depth Mode */}
          {crawlMode === 'indepth' && (
            <div>
              <label htmlFor="sitemap-url" className="block text-sm font-medium text-gray-700 mb-2">
                Sitemap URL (Optional)
              </label>
              <input
                type="url"
                id="sitemap-url"
                value={sitemapUrl}
                onChange={(e) => setSitemapUrl(e.target.value)}
                placeholder="https://example.com/sitemap.xml"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                disabled={loading}
              />
              <p className="text-xs text-gray-500 mt-1">
                Leave empty to auto-discover sitemap. Provide specific URL for custom sitemaps.
              </p>
            </div>
          )}

          <p className="text-sm text-gray-600">
            {crawlMode === 'quick'
              ? 'Quick mode analyzes the main page plus key navigation and footer links for faster processing.'
              : 'In-depth mode discovers and analyzes pages from the sitemap for comprehensive coverage.'
            }
          </p>
        </form>
      </div>

      {/* AI Provider Selection */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Settings className="h-6 w-6 text-purple-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">AI Provider & Model</h2>
              <p className="text-sm text-gray-600">
                {validationStatus[selectedProvider] === 'valid' && selectedModel
                  ? (
                    <span className="flex items-center">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-1" />
                      {selectedProvider.toUpperCase()} - {selectedModel.split('/').pop()}
                    </span>
                  )
                  : 'Click to configure AI provider and model'
                }
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            className={`px-4 py-2 rounded-md font-medium transition-colors ${
              validationStatus[selectedProvider] === 'valid' && selectedModel
                ? 'bg-green-100 text-green-800 border border-green-200'
                : 'bg-purple-600 text-white hover:bg-purple-700'
            }`}
            disabled={loading}
          >
            {validationStatus[selectedProvider] === 'valid' && selectedModel ? 'Change' : 'Configure'}
          </button>
        </div>
      </div>

      {/* Progress Indicator */}
      {loading && progress && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {progress.step === 'generating' ? 'AI is Working on Your Content' : 'Processing Your Website'}
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">{progress.message}</span>
              <span className="text-sm text-gray-500">{progress.progress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className={`h-3 rounded-full transition-all duration-500 ${
                  progress.step === 'generating'
                    ? 'bg-gradient-to-r from-purple-500 to-blue-500'
                    : 'bg-blue-600'
                }`}
                style={{ width: `${progress.progress}%` }}
              ></div>
            </div>
            {progress.step === 'generating' && (
              <div className="text-xs text-gray-500 italic">
                💡 Tip: AI generation typically takes 30-60 seconds depending on your website's complexity
              </div>
            )}
            {progress.step === 'error' && progress.error && (
              <div className="flex items-center space-x-2 text-red-600 text-sm">
                <AlertCircle className="h-4 w-4" />
                <span>{progress.error}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Generated Content Review */}
      {generatedContent && !loading && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Generated Content</h3>
            <div className="flex items-center space-x-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              <span className="text-sm text-green-600">Generation Complete</span>
            </div>
          </div>
          
          {editingContent ? (
            <div className="space-y-4">
              <textarea
                value={editedContent}
                onChange={(e) => setEditedContent(e.target.value)}
                rows={20}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
              />
              <div className="flex space-x-3">
                <button
                  onClick={handleSaveEdit}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200"
                >
                  Save Changes
                </button>
                <button
                  onClick={handleCancelEdit}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors duration-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4 border">
                <pre className="whitespace-pre-wrap text-sm text-gray-800 font-mono leading-relaxed max-h-96 overflow-y-auto">
                  {generatedContent}
                </pre>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={handleEditContent}
                  className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors duration-200"
                >
                  <Edit3 className="h-4 w-4 mr-2" />
                  Edit Content
                </button>
                <button
                  onClick={onFinalizeContent}
                  className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors duration-200"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Finalize & Download
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Generate Button */}
      {!generatedContent && (
        <div className="flex justify-center">
          <button
            onClick={handleSubmit}
            disabled={!isFormValid || loading}
            className="flex items-center px-8 py-4 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 text-lg font-medium"
          >
            {loading ? (
              <RefreshCw className="h-5 w-5 mr-3 animate-spin" />
            ) : (
              <ArrowRight className="h-5 w-5 mr-3" />
            )}
            {loading ? 'Generating...' : 'Generate llm.txt with AI'}
          </button>
        </div>
      )}

      {/* AI Provider Modal */}
      <AIProviderModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        selectedProvider={selectedProvider}
        selectedModel={selectedModel}
        apiKeys={apiKeys}
        onProviderChange={setSelectedProvider}
        onModelChange={setSelectedModel}
        onApiKeyChange={handleApiKeyChange}
        onValidateKey={handleValidateKey}
        validationStatus={validationStatus}
      />
    </div>
  );
}
